import { t } from '@glory/i18n';
import { Eye, EyeOff } from 'lucide-react';
import {
  type ComponentProps,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { cn } from '../lib/cn.ts';
import { mergeRefs } from '../lib/refs.ts';
import {
  Field,
  FieldAction,
  FieldBox,
  type FieldPart,
  type FieldSize,
  inputClass,
  splitField,
  useFieldControl,
} from './field.tsx';
import { normalizeEmail, suggestEmail } from './parsers/index.ts';

type NativeInputProps = Omit<
  ComponentProps<'input'>,
  'size' | 'value' | 'defaultValue' | 'onChange' | 'children'
>;

export interface TextInputProps extends NativeInputProps {
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly size?: FieldSize;
  readonly variant?: 'default' | 'cell';
  readonly leading?: ReactNode;
  readonly trailing?: ReactNode;
  readonly boxClassName?: string;
  /** Ошибка без `Field` вокруг: рамка подсвечивается. */
  readonly invalid?: boolean;
}

/** Однострочный ввод в рамке поля. Внутри `Field` связывается с подписью и ошибкой сам. */
export function TextInput({
  value,
  onValueChange,
  size = 'md',
  variant = 'default',
  leading,
  trailing,
  boxClassName,
  className,
  id,
  disabled,
  readOnly,
  invalid,
  ref,
  ...props
}: TextInputProps) {
  const control = useFieldControl({ id });
  return (
    <FieldBox
      size={size}
      variant={variant}
      leading={leading}
      trailing={trailing}
      className={boxClassName}
      disabled={disabled}
      readOnly={readOnly}
      invalid={invalid}
    >
      <input
        ref={ref}
        {...props}
        {...control}
        {...(invalid ? { 'aria-invalid': true } : {})}
        disabled={disabled ?? control.disabled}
        readOnly={readOnly ?? control.readOnly}
        className={cn(inputClass, className)}
        value={value}
        onChange={(e) => {
          onValueChange(e.target.value);
        }}
      />
    </FieldBox>
  );
}

/** Счётчик символов при лимите: виден всегда, при превышении — ошибка. */
function useLengthLimit(value: string, max: number | undefined, error: string | null | undefined) {
  if (max === undefined) return { aside: undefined, error };
  const count = [...new Intl.Segmenter('ru', { granularity: 'grapheme' }).segment(value)].length;
  const over = count > max;
  return {
    aside: <span className={cn(over && 'font-bold text-danger')}>{t('fields.counter', { count, max })}</span>,
    error: error ?? (over ? t('fields.tooLong', { max }) : undefined),
  };
}

export interface TextFieldProps extends FieldPart, Omit<TextInputProps, keyof FieldPart | 'maxLength'> {
  /** Лимит символов: счётчик под полем, превышение — ошибка (ввод не обрезается). */
  readonly maxLength?: number;
  /** Классы самого элемента ввода (`className` относится ко всему полю). */
  readonly inputClassName?: string;
}

export function TextField(props: TextFieldProps) {
  const [field, { maxLength, inputClassName, ...input }] = splitField(props);
  const limit = useLengthLimit(input.value, maxLength, field.error);
  return (
    <Field {...field} error={limit.error} aside={field.aside ?? limit.aside}>
      <TextInput {...input} className={inputClassName} />
    </Field>
  );
}

export interface PasswordFieldProps extends FieldPart, Omit<TextInputProps, keyof FieldPart | 'type'> {
  /** `current-password` — вход, `new-password` — новый пароль (менеджер паролей предложит сгенерировать). */
  readonly autoComplete?: 'current-password' | 'new-password';
}

/** Пароль: показать/скрыть, предупреждение о включённом Caps Lock. */
export function PasswordField(props: PasswordFieldProps) {
  const [field, { autoComplete = 'current-password', onKeyDown, onKeyUp, ...input }] = splitField(props);
  const [visible, setVisible] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const trackCapsLock = (e: KeyboardEvent<HTMLInputElement>) => {
    setCapsLock(e.getModifierState('CapsLock'));
  };
  return (
    <Field {...field} description={capsLock ? t('fields.capsLock') : field.description}>
      <TextInput
        {...input}
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        onKeyDown={(e) => {
          trackCapsLock(e);
          onKeyDown?.(e);
        }}
        onKeyUp={(e) => {
          trackCapsLock(e);
          onKeyUp?.(e);
        }}
        trailing={
          <>
            {input.trailing}
            <FieldAction
              label={visible ? t('ui.hidePassword') : t('ui.showPassword')}
              pressed={visible}
              onClick={() => {
                setVisible((v) => !v);
              }}
            >
              {visible ? <EyeOff /> : <Eye />}
            </FieldAction>
          </>
        }
      />
    </Field>
  );
}

export interface EmailFieldProps
  extends FieldPart, Omit<TextInputProps, keyof FieldPart | 'type' | 'inputMode'> {
  /** `username webauthn` на экране входа — браузер предложит passkey прямо из поля. */
  readonly autoComplete?: string;
}

/**
 * Email: при уходе с поля адрес приводится к виду для хранения (нижний регистр, без пробелов),
 * при опечатке в популярном домене — подсказка «Может быть, …?» одним нажатием.
 */
export function EmailField(props: EmailFieldProps) {
  const [field, { autoComplete = 'email', onBlur, onValueChange, value, ...input }] = splitField(props);
  const [left, setLeft] = useState(false);
  const suggestion = left ? suggestEmail(value) : null;
  const description =
    suggestion === null ? (
      field.description
    ) : (
      <button
        type="button"
        className="text-left font-semibold text-text underline decoration-accent decoration-2 underline-offset-2"
        onClick={() => {
          onValueChange(suggestion);
        }}
      >
        {t('fields.emailSuggestion', { email: suggestion })}
      </button>
    );
  return (
    <Field {...field} description={description}>
      <TextInput
        {...input}
        type="text"
        inputMode="email"
        autoComplete={autoComplete}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        value={value}
        onValueChange={(next) => {
          setLeft(false);
          onValueChange(next);
        }}
        onBlur={(e) => {
          const normalized = normalizeEmail(value);
          if (normalized !== value) onValueChange(normalized);
          setLeft(true);
          onBlur?.(e);
        }}
      />
    </Field>
  );
}

type NativeTextAreaProps = Omit<
  ComponentProps<'textarea'>,
  'value' | 'defaultValue' | 'onChange' | 'children'
>;

export interface TextAreaInputProps extends NativeTextAreaProps {
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly size?: FieldSize;
  /** Действия справа сверху; сюда встанет кнопка голосового ввода. */
  readonly trailing?: ReactNode;
  /** Выше этой высоты (px) появляется прокрутка. */
  readonly maxHeight?: number;
  readonly ref?: Ref<HTMLTextAreaElement>;
}

/** Многострочный ввод с автовысотой: растёт по содержимому до `maxHeight`. */
export function TextAreaInput({
  value,
  onValueChange,
  size = 'md',
  trailing,
  maxHeight = 320,
  rows = 3,
  className,
  id,
  disabled,
  readOnly,
  ref,
  ...props
}: TextAreaInputProps) {
  const control = useFieldControl({ id });
  const inner = useRef<HTMLTextAreaElement | null>(null);

  useLayoutEffect(() => {
    const el = inner.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${String(Math.min(el.scrollHeight, maxHeight))}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [value, maxHeight]);

  return (
    <FieldBox size={size} multiline trailing={trailing} disabled={disabled} readOnly={readOnly}>
      <textarea
        ref={mergeRefs(inner, ref)}
        rows={rows}
        {...props}
        {...control}
        disabled={disabled ?? control.disabled}
        readOnly={readOnly ?? control.readOnly}
        className={cn(inputClass, 'resize-none leading-[inherit]', className)}
        value={value}
        onChange={(e) => {
          onValueChange(e.target.value);
        }}
      />
    </FieldBox>
  );
}

export interface TextAreaFieldProps
  extends FieldPart, Omit<TextAreaInputProps, keyof FieldPart | 'maxLength'> {
  readonly maxLength?: number;
}

export function TextAreaField(props: TextAreaFieldProps) {
  const [field, { maxLength, ...input }] = splitField(props);
  const limit = useLengthLimit(input.value, maxLength, field.error);
  return (
    <Field {...field} error={limit.error} aside={field.aside ?? limit.aside}>
      <TextAreaInput {...input} />
    </Field>
  );
}
